import React from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import { hapticSelection } from '../utils/haptics';
import { formatDriverName } from '../utils/driverName';
import useScrollLock from '../utils/useScrollLock';
import { formatMoney, getFareSnapshot, getRideDistanceKm, getRideDurationMin, getFareDistanceKm } from '../utils/rideData';
import { fareRows, fareRowLabel } from '../utils/fareRows';

export default function RideDetailsModal({ ride, onClose, onRebook, onCallSupport }) {
  const { t } = useLanguage();
  useScrollLock(Boolean(ride));

  if (!ride) return null;

  const getTrans = (key, fallback) => {
    const val = t(key);
    if (!val || val === key || val.toUpperCase() === key.toUpperCase()) {
      return fallback;
    }
    return val;
  };

  const formatDate = (ts) => {
    if (!ts) return '';
    let d = null;
    if (ts?.toDate && typeof ts.toDate === 'function') d = ts.toDate();
    else if (ts instanceof Date) d = ts;
    else if (typeof ts === 'number') d = new Date(ts);
    else if (ts?.seconds) d = new Date(ts.seconds * 1000);
    else if (typeof ts === 'string') d = new Date(ts);
    if (!d || Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isCompleted = ride.status === 'completed';
  const shortId = (ride.id || 'N/A').slice(0, 8).toUpperCase();
  const fareSnapshot = getFareSnapshot(ride);
  const distanceKm = getRideDistanceKm(ride);
  const fareDistanceKm = getFareDistanceKm(ride) ?? distanceKm;
  const durationMin = getRideDurationMin(ride);

  return (
    <div className="modal-backdrop fade-in" onClick={onClose}>
      <div
        className="modal-card ride-details-modal slide-up"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={getTrans('details.title', 'Ride Details')}
      >
        {/* Header */}
        <div className="ride-details-header">
          <button type="button" className="btn-close-circle" onClick={onClose} aria-label={t('common.close')}>
            ✕
          </button>
          <div className="ride-details-title-box">
            <h3>{getTrans('details.title', 'Ride Details')}</h3>
            <span className="ride-details-id">#RD-{shortId}</span>
          </div>
        </div>

        <div className="ride-details-body">
          {/* Status & Fare Hero Card */}
          <div className="ride-details-hero-card">
            <span className={`status-pill status-${ride.status}`}>
              {getTrans(`status.${ride.status}`, ride.status === 'completed' ? 'Completed' : 'Cancelled')}
            </span>
            <div className="ride-details-hero-fare">
            {formatMoney(fareSnapshot?.total ?? ride.fare)} <span className="fare-method-tag">{getTrans('pay.cash', 'Cash')}</span>
            </div>
            <p className="ride-details-hero-date">{formatDate(ride.createdAt)}</p>
          </div>

          {/* Address Breakdown Card (Pin A -> Pin B) */}
          <div className="ride-details-card">
            <h4 className="ride-details-section-label">📍 {getTrans('details.addresses', 'Address Details')}</h4>
            <div className="ride-details-route">
              <div className="route-step pickup-step">
                <div className="route-marker-badge marker-a">A</div>
                <div className="route-text">
                  <strong>{ride.pickup?.name || ride.pickup?.address || 'Pickup Location'}</strong>
                  <p>{ride.pickup?.address || ''}</p>
                </div>
              </div>

              <div className="route-line-connector">
                <span className="route-line-dash" />
                <span className="route-distance-pill">
                  {fareDistanceKm != null ? `${fareDistanceKm.toFixed(1)} km` : getTrans('details.unavailable', 'Not available')} · {durationMin != null ? `${durationMin} mins` : getTrans('details.unavailable', 'Not available')}
                </span>
              </div>

              <div className="route-step drop-step">
                <div className="route-marker-badge marker-b">B</div>
                <div className="route-text">
                  <strong>{ride.destination?.name || ride.destination?.address || 'Drop Location'}</strong>
                  <p>{ride.destination?.address || ''}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Driver & Auto Card */}
          {(ride.driverName || ride.vehicleNumber) && (
            <div className="ride-details-card">
              <h4 className="ride-details-section-label">🛺 {getTrans('details.driverCard', 'Driver & Vehicle')}</h4>
              <div className="ride-details-driver-row">
                <div className="driver-avatar-circle">
                  {ride.driverName ? ride.driverName.replace(/^(Anna|Captain)\s+/i, '').trim().charAt(0).toUpperCase() : 'A'}
                </div>
                <div className="driver-info">
                  <strong>{formatDriverName(ride.driverName)}</strong>
                  <p>{ride.driverRatingAvg ? `⭐ ${ride.driverRatingAvg} · ` : ''}{getTrans('driver.verified', 'Verified Anna')}</p>
                </div>
                {ride.vehicleNumber && (
                  <div className="vehicle-plate-badge">
                    {ride.vehicleNumber}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Fare Summary Breakdown */}
          <div className="ride-details-card">
            <h4 className="ride-details-section-label">📄 {getTrans('details.fareSummary', 'Fare Summary')}</h4>
            <div className="fare-summary-list">
              {fareRows(fareSnapshot).rows.map((row) => (
                <div className="fare-row" key={row.id}>
                  <span>{fareRowLabel(row, t)}</span>
                  <span>{formatMoney(row.amount)}</span>
                </div>
              ))}
              <div className="fare-row fare-row-total">
                <strong>{getTrans('fare.totalCash', 'Total Cash Fare')}</strong>
                <strong className="fare-total-val">{formatMoney(fareSnapshot?.total ?? ride.fare)}</strong>
              </div>
            </div>
            {!fareSnapshot && <p className="text-muted" style={{ marginTop: 8, fontSize: 12 }}>{getTrans('details.fareBreakdownUnavailable', 'Fare breakdown unavailable for this older ride.')}</p>}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="ride-details-footer">
          {isCompleted && ride.destination?.lat && (
            <button
              type="button"
              className="btn btn-primary btn-block ride-rebook-btn"
              onClick={() => {
                hapticSelection();
                onRebook(ride);
              }}
            >
              🛺 {getTrans('details.rebookRoute', 'Re-book This Route')}
            </button>
          )}
          <button
            type="button"
            className="btn btn-outline btn-block ride-support-btn"
            onClick={() => {
              hapticSelection();
              onCallSupport?.();
            }}
          >
            🎧 {getTrans('details.needHelp', 'Need Help / Call Support')}
          </button>
        </div>
      </div>
    </div>
  );
}
